import React from 'react';
import { useElapsed, formatSeconds } from './useElapsed';

interface LiveBadgeProps {
  isLive: boolean;
  reconnecting?: boolean;
  // UTC ISO instant the session went live (live_state.started_at). When present
  // and live, the badge shows the running elapsed time for time management.
  startedAt?: string | null;
  // Duration in seconds of the most recent completed session
  // (live_state.last_session_seconds). When not live, shown as the frozen final
  // time so the recorded duration remains visible after stopping.
  lastSessionSeconds?: number | null;
}

// Pulsing LIVE indicator shown on the slide stage when the session is live.
// While live, an elapsed timer (started_at → now) is shown next to LIVE so the
// presenter can manage time.
const LiveBadge: React.FC<LiveBadgeProps> = ({ isLive, reconnecting, startedAt, lastSessionSeconds }) => {
  const elapsed = useElapsed(startedAt, isLive);

  if (!isLive) {
    return (
      <span className="live-badge live-badge-off" role="status">
        OFFLINE
        {typeof lastSessionSeconds === 'number' && lastSessionSeconds > 0 && (
          <span className="live-badge-elapsed" aria-label={`Last session ${formatSeconds(lastSessionSeconds)}`}>
            {formatSeconds(lastSessionSeconds)}
          </span>
        )}
      </span>
    );
  }
  return (
    <span className={`live-badge${reconnecting ? ' live-badge-reconnecting' : ''}`} role="status">
      <span className="live-badge-dot" aria-hidden="true" />
      {reconnecting ? 'RECONNECTING' : 'LIVE'}
      {elapsed && !reconnecting && (
        <span className="live-badge-elapsed" aria-label={`Elapsed ${elapsed}`}>{elapsed}</span>
      )}
    </span>
  );
};

export default LiveBadge;
